import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Users,
  ShieldCheck,
  Eye,
} from "lucide-react";

const roleBadge = {
  admin: { label: "Administrador", cls: "bg-red-100 text-red-800 border-red-200", Icon: ShieldCheck },
  viewer: { label: "Visualizador", cls: "bg-blue-100 text-blue-800 border-blue-200", Icon: Eye },
};

const emptyForm = { email: "", name: "", role: "viewer", password: "" };

export default function ManageUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch {
      toast.error("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({ email: u.email, name: u.name || "", role: u.role, password: "" });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.email.trim()) {
      toast.error("Email é obrigatório");
      return;
    }
    if (!editingId && (!form.password || form.password.length < 6)) {
      toast.error("Senha deve ter ao menos 6 caracteres");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        email: form.email.trim(),
        name: form.name.trim(),
        role: form.role,
      };
      if (form.password) payload.password = form.password;
      if (editingId) {
        await api.put(`/users/${editingId}`, payload);
        toast.success("Usuário atualizado");
      } else {
        await api.post("/users", payload);
        toast.success("Usuário criado");
      }
      setModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (u.id === me?.id) {
      toast.error("Você não pode excluir sua própria conta");
      return;
    }
    if (!window.confirm(`Excluir o usuário ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuário excluído");
      setUsers((s) => s.filter((x) => x.id !== u.id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao excluir");
    }
  };

  return (
    <div className="space-y-8" data-testid="manage-users">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-700 font-semibold">Equipe</div>
          <h1 className="font-display text-4xl font-bold mt-2">Usuários e permissões</h1>
          <p className="text-stone-600 mt-2">
            Cadastre pessoas com acesso ao painel. <strong>Administradores</strong> podem editar tudo.{" "}
            <strong>Visualizadores</strong> só consultam.
          </p>
        </div>
        <Button
          onClick={openNew}
          className="bg-gradient-to-r from-red-700 to-orange-600 hover:from-red-800 hover:to-orange-700 text-white rounded-full px-6"
          data-testid="new-user-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Novo usuário
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
      ) : users.length === 0 ? (
        <Card className="border-dashed border-stone-300 rounded-2xl">
          <CardContent className="py-16 text-center">
            <Users className="h-10 w-10 text-stone-400 mx-auto" />
            <div className="font-display text-xl mt-4 text-stone-700">Nenhum usuário</div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-stone-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200 text-stone-600 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-6 py-3">Nome</th>
                  <th className="text-left px-6 py-3">Email</th>
                  <th className="text-left px-6 py-3">Perfil</th>
                  <th className="text-left px-6 py-3">Criado em</th>
                  <th className="text-right px-6 py-3">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {users.map((u) => {
                  const r = roleBadge[u.role] || roleBadge.viewer;
                  const isMe = u.id === me?.id;
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td className="px-6 py-3 font-medium text-stone-900">
                        {u.name || "—"}
                        {isMe && (
                          <span className="ml-2 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-stone-100 text-stone-600 border border-stone-200">
                            você
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-stone-600">{u.email}</td>
                      <td className="px-6 py-3">
                        <Badge className={`${r.cls} hover:${r.cls} inline-flex items-center gap-1`}>
                          <r.Icon className="h-3 w-3" /> {r.label}
                        </Badge>
                      </td>
                      <td className="px-6 py-3 text-stone-500 whitespace-nowrap">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString("pt-BR") : "—"}
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEdit(u)}
                            className="rounded-full"
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => remove(u)}
                            disabled={isMe}
                            className="rounded-full text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800 disabled:opacity-40"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editingId ? "Editar usuário" : "Novo usuário"}
            </DialogTitle>
            <DialogDescription>
              Administradores editam tudo · Visualizadores só consultam.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="u-name">Nome</Label>
              <Input
                id="u-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Maria da Silva"
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label htmlFor="u-email">Email</Label>
              <Input
                id="u-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="usuario@renascer.com"
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label htmlFor="u-role">Perfil</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger id="u-role" className="mt-1" data-testid="user-role-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador (edita tudo)</SelectItem>
                  <SelectItem value="viewer">Visualizador (só consulta)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="u-pass">Senha {editingId && <span className="text-stone-400">(deixe em branco para manter)</span>}</Label>
              <Input
                id="u-pass"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editingId ? "•••••••• (opcional)" : "Mínimo 6 caracteres"}
                data-testid="user-password-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-gradient-to-r from-red-700 to-orange-600 hover:from-red-800 hover:to-orange-700 text-white"
              data-testid="save-user-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
